import { useState } from 'react';
import { T, useGT } from 'gt-react';
import {
  ArrowClockwiseIcon,
  CaretDownIcon,
  CaretUpIcon,
  CheckCircleIcon,
  ClockIcon,
  WarningCircleIcon,
  XIcon,
} from '@phosphor-icons/react';

import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/common/CopyButton';
import { Progress } from '@/components/ui/progress';
import { Spinner } from '@/components/ui/spinner';
import {
  isJobActive,
  isJobRunning,
  useBackgroundJobs,
  type BackgroundJob,
} from '@/context/background-jobs';

/** Status icon for one job, matching the four statuses one for one. */
function JobStatusIcon({ status }: { status: BackgroundJob['status'] }) {
  if (status === 'running') return <Spinner size="sm" />;
  if (status === 'queued') {
    return <ClockIcon className="size-4 shrink-0 text-muted-foreground" />;
  }
  if (status === 'succeeded') {
    return (
      <CheckCircleIcon
        className="size-4 shrink-0 text-green-600"
        weight="fill"
      />
    );
  }
  return (
    <WarningCircleIcon
      className="size-4 shrink-0 text-destructive"
      weight="fill"
    />
  );
}

/**
 * The bar for a running job.
 *
 * Determinate only when the job reported a real fraction. Without one, a full
 * bar that pulses reads as "working" without claiming a percentage — the same
 * choice the conversion page made before this panel existed.
 */
function JobProgressBar({ percent }: { percent: number | undefined }) {
  if (percent === undefined) {
    return (
      <Progress
        value={100}
        className="h-1 animate-pulse"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
      />
    );
  }
  return (
    <Progress
      value={percent}
      className="h-1"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percent}
    />
  );
}

/**
 * What a failed job says, which is also what its copy button copies.
 *
 * Shared so the two can't drift: the text on screen is exactly the text pasted
 * into a bug report.
 */
function useFailureMessage(job: BackgroundJob): string {
  const gt = useGT();
  const fallback = gt('Something went wrong.', {
    context: 'background task row status when the failure carried no message',
  });
  return job.errorMessage ?? fallback;
}

/** The status line under a row's title: the job's own label, or its error. */
function JobLabel({ job }: { job: BackgroundJob }) {
  const gt = useGT();
  const failureMessage = useFailureMessage(job);

  if (job.status === 'failed') {
    return <span className="text-xs text-destructive">{failureMessage}</span>;
  }
  if (job.status === 'queued') {
    return (
      <span className="text-xs text-muted-foreground">
        {gt('Waiting…', {
          context:
            'background task row status while the job waits for a free slot',
        })}
      </span>
    );
  }
  return <span className="text-xs text-muted-foreground">{job.label}</span>;
}

function JobRow({ job }: { job: BackgroundJob }) {
  const gt = useGT();
  const { retryJob, dismissJob } = useBackgroundJobs();
  const running = isJobRunning(job);
  const failureMessage = useFailureMessage(job);

  return (
    <div className="flex flex-col gap-1.5 px-3 py-2">
      <div className="flex items-center gap-2">
        <JobStatusIcon status={job.status} />
        <span className="min-w-0 flex-1 truncate text-sm" title={job.title}>
          {job.title}
        </span>
        {job.action && (
          <Button
            variant="link"
            size="sm"
            className="h-auto shrink-0 p-0 text-xs"
            onClick={job.action.run}
          >
            {job.action.label}
          </Button>
        )}
        {job.status === 'failed' && (
          // The error text is the one diagnostic worth having in a bug report,
          // and reading it off a small row to retype is hopeless.
          <CopyButton
            value={`${job.title}: ${failureMessage}`}
            title={gt('Copy error details', {
              context: 'copy the failure message from a background task row',
            })}
            className="size-6 shrink-0 p-0"
            iconClassName="size-3.5"
          />
        )}
        {job.status === 'failed' && job.retryable && (
          <Button
            variant="ghost"
            size="icon"
            className="size-6 shrink-0"
            aria-label={gt('Retry', {
              context: 'retry a failed background task',
            })}
            onClick={() => retryJob(job.id)}
          >
            <ArrowClockwiseIcon className="size-3.5" />
          </Button>
        )}
        {/* Running jobs cannot be dismissed: the row is the only handle on work
            that is still going, and hiding it would not stop it. */}
        {!running && (
          <Button
            variant="ghost"
            size="icon"
            className="size-6 shrink-0"
            aria-label={gt('Dismiss', {
              context: 'remove a finished background task from the panel',
            })}
            onClick={() => dismissJob(job.id)}
          >
            <XIcon className="size-3.5" />
          </Button>
        )}
      </div>
      {running && <JobProgressBar percent={job.percent} />}
      <JobLabel job={job} />
    </div>
  );
}

/** Header icon: what the panel as a whole is doing. */
function PanelStatusIcon({ jobs }: { jobs: BackgroundJob[] }) {
  if (jobs.some(isJobActive)) return <Spinner size="sm" />;
  if (jobs.some((job) => job.status === 'failed')) {
    return (
      <WarningCircleIcon
        className="size-4 shrink-0 text-destructive"
        weight="fill"
      />
    );
  }
  return (
    <CheckCircleIcon className="size-4 shrink-0 text-green-600" weight="fill" />
  );
}

/**
 * Sticky tracker for work started elsewhere in the app.
 *
 * Mounted once, outside the router outlet, so it keeps rendering as the user
 * navigates — which is the entire point: an upload or conversion can be left to
 * run while its page is gone. Renders nothing when there is nothing to track.
 *
 * Collapsing is user-controlled and never automatic. A failure while collapsed
 * shows in the header icon rather than springing the panel open, since a panel
 * that reopens itself over what someone is reading is worse than a red icon.
 */
export function BackgroundJobsPanel() {
  const gt = useGT();
  const { jobs, dismissFinishedJobs } = useBackgroundJobs();
  const [collapsed, setCollapsed] = useState(false);

  if (jobs.length === 0) return null;

  const hasFinishedJobs = jobs.some((job) => !isJobActive(job));

  return (
    <div
      // Fixed and bottom-right, so it never occupies layout space. Sits clear of
      // the sidenav (which is on the left) and above the page's own content.
      className="fixed bottom-3 right-3 z-50 w-80 overflow-hidden rounded-lg border bg-popover shadow-lg"
      role="region"
      aria-label={gt('Background tasks', {
        context: 'accessible name of the background task tracker panel',
      })}
    >
      <div className="flex items-center gap-2 border-b bg-muted/50 px-3 py-2">
        <PanelStatusIcon jobs={jobs} />
        <span className="min-w-0 flex-1 truncate text-sm font-medium">
          <T context="heading of the background task tracker panel">
            Background tasks
          </T>
          {` (${String(jobs.length)})`}
        </span>
        {hasFinishedJobs && (
          <Button
            variant="ghost"
            size="sm"
            className="h-auto shrink-0 px-1 py-0 text-xs"
            onClick={dismissFinishedJobs}
          >
            <T context="button clearing all finished rows from the background task panel">
              Clear finished
            </T>
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="size-6 shrink-0"
          aria-label={
            collapsed
              ? gt('Expand background tasks', {
                  context: 'expand the collapsed background task panel',
                })
              : gt('Collapse background tasks', {
                  context: 'collapse the background task panel to its header',
                })
          }
          aria-expanded={!collapsed}
          onClick={() => setCollapsed((current) => !current)}
        >
          {collapsed ? (
            <CaretUpIcon className="size-3.5" />
          ) : (
            <CaretDownIcon className="size-3.5" />
          )}
        </Button>
      </div>
      {!collapsed && (
        <div className="max-h-64 divide-y overflow-y-auto">
          {jobs.map((job) => (
            <JobRow key={job.id} job={job} />
          ))}
        </div>
      )}
    </div>
  );
}
